/* eslint-disable @typescript-eslint/await-thenable */
import { WarningTriangle } from '@elliemae/ds-icons';
import { TooltipTextProvider } from '@elliemae/ds-truncated-tooltip-text';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';
import {
  dispatchOriginData,
  dispatchUserData,
  getInitData,
  IS_INVALID_CREDENTIAL,
  IS_INVALID_TOKEN,
  IS_RECONCILE,
  setMockInitData,
} from '../common/app-helpers';
import Common from '../common/services/Common';
import Session, {
  ALL_SUMMARY_PARAMS,
  IS_BUYSIDE,
  IS_JWT_TOKEN,
  IS_LO,
  IS_LOCK_REQUEST_FORM_LOCK_EXTENSION,
  IS_SECONDARY_LOCK,
  IS_SELLSIDE,
  IS_STANDALONE,
} from '../common/services/Session';
import { buysideRequested } from '../common/utils/constants/ErrorCodes';
import { startLog } from '../common/utils/logger';
import {
  openGlobalErrorModalAction,
  openNoLoanAmountErrorModal,
} from '../data/globalErrorModal/actionCreators';
import { lookupsActions } from '../store/lookups/index';
import { LockConfirmWrapper } from '../view/LockConfirm/LockConfirmWrapper';
import { LockExtension } from '../view/LockExtension/LockExtension';
import { LockSummary } from '../view/LockSummary/LockSummary';
import { Login } from '../view/Login';
import { SearchForm } from '../view/SearchForm/SearchForm';
import { SearchResult } from '../view/SearchResult';
import { Header } from '../view/components/Header/index';
import {
  StyledEppsContainerBody,
  StyledEppsContainerHeader,
  StyledEppsParentContainer,
} from '../view/global/styles/styled';
import {
  PATH_LOCK_EXTENSION,
  PATH_LOCK_SUMMARY,
  PATH_LOGIN,
  PATH_SEARCH_FOR_PRODUCTS,
  PATH_SEARCH_RESULTS,
} from './constants';
import { ScreenLoader } from '../view/components/ScreenLoader';
import { GlobalErrorModal } from '../view/components/GlobalErrorModal';

const App: React.FC = () => {
  const [moveNext, setMoveNext] = useState(false);
  const [isError, setIsError] = useState(false);
  const [initData, setInitData] = useState(undefined);
  const [isLoggedIn, setIsLoggedIn] = useState(false); // State to track login status
  const navigate = useNavigate();
  const isBuyside = Session.get(IS_BUYSIDE);
  const isSellside = Session.get(IS_SELLSIDE);
  const isSecondaryLock = Session.get(IS_SECONDARY_LOCK);
  const isLockRequestForm = Session.get(IS_LOCK_REQUEST_FORM_LOCK_EXTENSION);
  const isJWTToken = Session.get(IS_JWT_TOKEN);

  const isLO = Session.get(IS_LO);
  const dispatch = useDispatch();

  const fetchData = async (isRefresh = false) => {
    const data = await getInitData(
      dispatch,
      isRefresh ?? false,
      setMoveNext,
      setIsError,
    );
    if (data.code === undefined) {
      dispatchUserData(dispatch);
      await setUserClientRoles();
      dispatch(lookupsActions.getLookupsDataAction({ dispatch } as any));
    }
    await setInitData(data);
  };

  useEffect(() => {
    startLog(buysideRequested);
    Session.set(ALL_SUMMARY_PARAMS, {});

    if (!Session.get(IS_STANDALONE)) {
      fetchData();
    } else {
      // TODO(ngen): Might need to do more work to support this mock data flow
      setMockInitData(dispatch);
      setMoveNext(true);
    }
  }, []);

  useEffect(() => {
    if (isError) {
      dispatch(
        openGlobalErrorModalAction({
          onCloseCallback: () => Common.closeApplication(),
        }) as any,
      );
    }
  }, [isError]);

  const setUserClientRoles = async () => {
    const [, rightsResponse] = await Promise.all([
      Common.setUserRoles(),
      Common.setClientRoles(),
    ]);
    if (!rightsResponse) {
      dispatch(
        openGlobalErrorModalAction({
          title: 'You have not been assigned a role!',
          errorMessage:
            'You will have limited access to any features until you have been assigned a role. Please contact your administrator.',
          icon: <WarningTriangle size="xxl" color={['warning', '600']} />,
        }) as any,
      );
    }
  };

  useEffect(() => {
    if (initData) {
      const {
        notTokenCredResource,
        errorLoanAmountBlank,
        hasLoanAmount,
        loanAmountBlank,
        lienPosition,
        originData,
        code,
        isRedirectToError,
      } = initData;
      if (!notTokenCredResource) {
        if (errorLoanAmountBlank || !hasLoanAmount) {
          dispatch(openNoLoanAmountErrorModal() as any);
        }
        if (!loanAmountBlank && lienPosition && hasLoanAmount) {
          dispatchOriginData(dispatch, originData, setMoveNext);
        }
      }
      if (initData[IS_RECONCILE] === IS_RECONCILE) {
        fetchData(true);
      }
      if (isRedirectToError) {
        setMoveNext(true);
        setIsError(true);
      }
      if (code && code !== IS_INVALID_TOKEN && code !== IS_INVALID_CREDENTIAL) {
        throw new Error();
      }
    }
  }, [initData]);

  useEffect(() => {
    if (!isLO && !isJWTToken) {
      return navigate(PATH_LOGIN);
    }

    if (isSecondaryLock || isLockRequestForm) {
      return navigate(PATH_LOCK_EXTENSION);
    }

    return navigate(PATH_SEARCH_RESULTS);
  }, []);

  // Callback function to handle successful login
  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
    navigate('/search-results');
  };

  const renderContent = useMemo(() => {
    return (
      <TooltipTextProvider>
        <StyledEppsContainerBody>
          <Routes>
            <Route path={PATH_SEARCH_FOR_PRODUCTS} element={<SearchForm />} />
            <Route
              path={PATH_LOCK_SUMMARY}
              element={
                isBuyside || isSellside ? (
                  <LockConfirmWrapper />
                ) : (
                  <LockSummary />
                )
              }
            />
            <Route path={PATH_LOCK_EXTENSION} element={<LockExtension />} />

            {/* Login Route */}
            <Route
              path="/login"
              element={<Login onLoginSuccess={handleLoginSuccess} />} // Pass the callback
            />

            {/* Search Results Route */}
            <Route path="/search-results" element={<SearchResult />} />
          </Routes>
        </StyledEppsContainerBody>
      </TooltipTextProvider>
    );
  }, [isLoggedIn]);

  const Landing = (Content: JSX.Element) => {
    return (
      <>
        <StyledEppsParentContainer>
          <StyledEppsContainerHeader>
            <Header />
          </StyledEppsContainerHeader>
          {Content}
        </StyledEppsParentContainer>
        <ScreenLoader />
        <GlobalErrorModal />
      </>
    );
  };

  return Landing(renderContent);
};

export default App;
